import { useState } from 'react';
import type { AssetKind } from '@avs/contract';
import { apiClient } from '../api/client.js';
import { useApp } from '../state/AppContext.js';
import { useApiCall } from '../hooks/useApi.js';
import { showToast } from '../hooks/useToasts.js';
import { uploadOneFile } from '../utils/uploadFlow.js';
import { Icon } from '../icons/Icon.js';
import { Card, CardHead } from '../components/Card.js';
import { Button } from '../components/Button.js';
import { EmptyState } from '../components/EmptyState.js';
import { SegmentedControl } from '../components/SegmentedControl.js';
import { relativeTime } from '../utils/time.js';

const TABS = [
  { key: 'video', label: '视频',   icon: 'video' as const },
  { key: 'image', label: '图片',   icon: 'image' as const },
  { key: 'group', label: '图片组', icon: 'folder' as const },
] as const;

export function Assets() {
  const { state } = useApp();
  const [tab, setTab] = useState<AssetKind>('video');
  const { data, loading, refresh } = useApiCall(
    () => apiClient.listAssets({ kind: tab, limit: 50 }),
    [tab],
  );
  const items = data?.items ?? [];

  const [uploading, setUploading] = useState(false);
  const [uploadHint, setUploadHint] = useState('');

  async function uploadFile(targetKind: 'video' | 'image') {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.multiple = true;
    inp.accept = targetKind === 'video' ? 'video/*' : 'image/*';
    inp.onchange = async () => {
      const files = Array.from(inp.files ?? []);
      if (!files.length) return;
      setUploading(true);
      let okCount = 0;
      let firstErr: string | null = null;
      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i]!;
          const mb = (file.size / 1048576).toFixed(1);
          setUploadHint(`上传中 ${i + 1}/${files.length}：${file.name}（${mb} MB）${file.size > 30 * 1048576 ? ' · 文件较大，请耐心等待' : ''}`);
          const r = await uploadOneFile(file, targetKind);
          if (r.ok) okCount++; else if (!firstErr) firstErr = r.error;
        }
      } finally {
        setUploading(false);
        setUploadHint('');
      }
      if (okCount > 0) showToast(`已上传 ${okCount} 个文件`, 'ok');
      if (firstErr) showToast('上传失败：' + firstErr, 'err');
      if (okCount > 0) {
        if (tab !== targetKind) setTab(targetKind); else refresh();
      }
    };
    inp.click();
  }

  return (
    <Card>
      <CardHead
        title="资源库"
        desc="管理视频、图片和图片组；选择资源后可在新建任务表单中复用。"
        right={
          <div className="row">
            <Button
              leftIcon={<Icon name="uploadVideo" size={16} />}
              disabled={uploading}
              onClick={() => void uploadFile('video')}
            >上传视频</Button>
            <Button
              leftIcon={<Icon name="addImage" size={16} />}
              disabled={uploading}
              onClick={() => void uploadFile('image')}
            >上传图片</Button>
            <Button
              leftIcon={<Icon name="refresh" size={16} />}
              onClick={() => { refresh(); showToast('已刷新'); }}
            >刷新</Button>
          </div>
        }
      />

      {uploading && (
        <div className="alert" style={{ marginBottom: 'var(--space-s)' }}>
          <Icon name="refresh" size={18} className="spin" />
          <div className="alert-body">{uploadHint || '上传中…'}</div>
        </div>
      )}

      <SegmentedControl items={TABS} value={tab} onChange={setTab} />

      <div style={{ marginTop: 'var(--space-s)' }}>
        {loading && items.length === 0 ? (
          <EmptyState icon="refresh" title="加载中" sub="正在拉取资源…" variant="plain" />
        ) : items.length === 0 ? (
          <EmptyState
            icon="folder"
            title="暂无资源"
            sub={tab === 'group'
              ? '可在「图片」段选中多张后生成图片组（待实现）。'
              : '点击右上角上传，或配置 TOS 后由 BFF 同步存量文件。'}
          />
        ) : (
          <div className="asset-grid">
            {items.map((a) => (
              <div key={a.id} className="asset-card">
                <div className="asset-thumb">
                  {a.previewUrl && a.kind === 'image' ? (
                    <img className="asset-media" src={a.previewUrl} alt={a.name} loading="lazy" />
                  ) : a.previewUrl && a.kind === 'video' ? (
                    <video className="asset-media" src={`${a.previewUrl}#t=0.3`} muted preload="metadata" playsInline />
                  ) : (
                    <Icon name={a.kind === 'video' ? 'video' : a.kind === 'group' ? 'folder' : 'image'} size={28} />
                  )}
                </div>
                <div className="asset-meta">
                  <div className="asset-name" title={a.name}>{a.name}</div>
                  <div className="asset-sub">{a.sizeText} · {relativeTime(a.createdAt)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
